import React, { useState, useEffect } from 'react';
import { getPermisoById, createPermiso, updatePermiso } from '../services/permisoService';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, ArrowLeft, Shield, RefreshCw } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';

export default function PermisoForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        nombre: '',
        modulo: '',
        descripcion: ''
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            cargarPermiso();
        }
    }, [id]);

    const cargarPermiso = async () => {
        try {
            setLoading(true);
            const data = await getPermisoById(id);
            setFormData({
                nombre: data.nombre,
                modulo: data.modulo || '',
                descripcion: data.descripcion || ''
            });
        } catch (error) {
            console.error('Error al cargar el permiso:', error);
            toast.error('Error al cargar los datos del permiso');
            navigate('/seguridad/permisos');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.nombre.trim()) {
            toast.error('La clave del permiso es requerida');
            return;
        }

        if (!formData.modulo.trim()) {
            toast.error('El módulo es requerido');
            return;
        }

        try {
            setLoading(true);
            if (isEditMode) {
                await updatePermiso(id, formData);
                toast.success('Permiso actualizado exitosamente');
            } else {
                await createPermiso(formData);
                toast.success('Permiso creado exitosamente');
            }
            // Logout and login might be needed if user is editing their own permissions, 
            // but for system-wide CRUD, just navigating back is fine.
            setTimeout(() => navigate('/seguridad/permisos'), 1000);
        } catch (error) {
            console.error('Error al guardar:', error);
            toast.error('Error al guardar el permiso');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto bg-white shadow-sm border border-gray-200 mb-6">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-slate-700 p-3 flex items-center gap-3 text-white">
                <button
                    onClick={() => navigate('/seguridad/permisos')}
                    className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                    title="Volver a la lista"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-400" />
                    <h1 className="text-lg font-semibold tracking-tight uppercase">
                        {isEditMode ? 'Mantenimiento: Editar Permiso' : 'Mantenimiento: Nuevo Permiso'}
                    </h1>
                </div>
            </div>

            {/* Form */}
            <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
                                Clave del Permiso <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                required
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-mono"
                                placeholder="Ej: socios.read"
                                value={formData.nombre}
                                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                            />
                            <p className="text-[10px] text-gray-400 mt-1 uppercase italic">Formato recomendado: objeto.accion (ej: caja.write)</p>
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
                                Módulo / Categoría <span className="text-red-500">*</span>
                            </label>
                            <select
                                required
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                                value={formData.modulo}
                                onChange={(e) => setFormData({ ...formData, modulo: e.target.value })}
                            >
                                <option value="">SELECCIONE MÓDULO...</option>
                                <option value="SEGURIDAD">SEGURIDAD</option>
                                <option value="SOCIOS">SOCIOS</option>
                                <option value="CAJA">CAJA</option>
                                <option value="CREDITOS">CRÉDITOS</option>
                                <option value="AHORROS">AHORROS</option>
                                <option value="CONTABILIDAD">CONTABILIDAD</option>
                                <option value="CONFIGURACION">CONFIGURACION</option>
                                <option value="GENERAL">GENERAL</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2 uppercase">
                            Descripción
                        </label>
                        <textarea
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all h-24"
                            placeholder="Describa brevemente la función de este permiso..."
                            value={formData.descripcion}
                            onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                        />
                    </div>

                    <div className="pt-4 border-t border-gray-200 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => navigate('/seguridad/permisos')}
                            className="px-4 py-1.5 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-100 transition-colors flex items-center text-sm font-medium"
                        >
                            <X className="w-3.5 h-3.5 mr-1.5" />
                            CANCELAR
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-1.5 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors flex items-center text-sm font-medium disabled:opacity-70 shadow-sm"
                        >
                            {loading ? (
                                <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            ) : (
                                <Save className="w-3.5 h-3.5 mr-1.5" />
                            )}
                            {isEditMode ? 'GUARDAR PERMISO' : 'CREAR PERMISO'}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
}
